import { FacilityReverseParams } from 'app/models/facility-reverse-params'
import {
  approveSelectedFacilityReverse,
  rejectSelectedFacilityReverse,
  deleteSelectedFacilityReverse,
  saveFacilityReverse,
  submitForApproval,
  loadAllFacilityReverses,
} from 'app/state/ducks/facility-reverses/thunks'
import {
  ErrorNotification,
  SuccessNotification,
} from 'app/views/components/notifications/notification'
import { Dispatch } from 'redux'

export async function approveFacilityReverse(
  facilityReverse: FacilityReverseParams,
  dispatch: Dispatch<any>,
) {
  const response: any = await dispatch(
    approveSelectedFacilityReverse(facilityReverse),
  )
  if (response.success) {
    SuccessNotification({
      title: 'Approved',
      message: 'Facility Reverse approved; the transaction has been reverted.',
    })
  } else {
    ErrorNotification({
      title: 'Approval Failed',
      message: response.payload ?? 'Could not approve Facility Reverse',
    })
  }
  dispatch(loadAllFacilityReverses())
  return response.success
}

export async function submitFacilityReverse(
  facilityReverse: FacilityReverseParams,
  dispatch: Dispatch<any>,
  // Composed flows below turn this off and report the outcome of the whole
  // sequence instead, so the user gets one notification, not a step-by-step.
  withMessages = true,
) {
  const response: any = await dispatch(submitForApproval(facilityReverse))
  if (response.success && withMessages) {
    SuccessNotification({
      title: 'Submitted for Approval',
      message: 'Facility Reverse has been sent for manager approval',
    })
  } else if (!response.success && withMessages) {
    ErrorNotification({
      title: 'Submit Failed',
      message: response.payload ?? 'Could not submit Facility Reverse',
    })
  }
  dispatch(loadAllFacilityReverses())
  return response.success
}

export async function rejectFacilityReverse(
  facilityReverse: FacilityReverseParams,
  dispatch: Dispatch<any>,
  withMessages = true,
) {
  const response: any = await dispatch(
    rejectSelectedFacilityReverse(facilityReverse),
  )
  if (response.success && withMessages) {
    SuccessNotification({
      title: 'Successful Rejection',
      message: 'Facility Reverse has been moved back to a Draft',
    })
  } else if (!response.success && withMessages) {
    ErrorNotification({
      title: 'Rejection Failed',
      message: response.payload ?? 'Could not reject Facility Reverse',
    })
  }
  dispatch(loadAllFacilityReverses())
  return response.success
}

/**
 * Clears the draft the ledger leaves behind after a rejection. Only called once
 * the reject itself has succeeded — deleting on a failed rejection would drop
 * a reversal that is still live.
 */
export async function deleteDraftFacilityReverse(
  facilityReverse: FacilityReverseParams,
  dispatch: Dispatch<any>,
  withMessages = true,
) {
  const response: any = await dispatch(
    deleteSelectedFacilityReverse(facilityReverse),
  )
  if (withMessages) {
    if (response.success) {
      SuccessNotification({
        title: 'Reversal Deleted',
        message:
          'The drafted reversal has been removed. The transaction can be reversed again.',
      })
    } else {
      ErrorNotification({
        title: 'Delete Failed',
        message: response.payload ?? 'Could not delete the drafted reversal.',
      })
    }
  }
  dispatch(loadAllFacilityReverses())
  return response.success
}

/**
 * Reject, then remove the draft the ledger reverts to. The delete is gated on
 * the rejection actually succeeding.
 */
export async function rejectAndRemoveFacilityReverse(
  facilityReverse: FacilityReverseParams,
  dispatch: Dispatch<any>,
) {
  const rejected = await rejectFacilityReverse(facilityReverse, dispatch, false)
  if (!rejected) {
    ErrorNotification({
      title: 'Rejection Failed',
      message:
        'Could not reject the reversal. The transaction is unchanged and the reversal is still awaiting approval.',
    })
    return false
  }

  const removed = await deleteDraftFacilityReverse(
    facilityReverse,
    dispatch,
    false,
  )
  if (!removed) {
    // The rejection stands; only the cleanup failed. Say so precisely — the
    // leftover draft still blocks the transaction from being reverted again.
    ErrorNotification({
      title: 'Reversal Rejected — Cleanup Failed',
      message:
        'The reversal was rejected, but its draft could not be removed. Delete it from Servicing to re-enable reverting this transaction.',
    })
    return false
  }

  SuccessNotification({
    title: 'Reversal Rejected',
    message:
      'The reversal has been rejected and removed. The transaction stands and can be reversed again.',
  })
  return true
}

/**
 * A reversal is only actionable once submitted, so the draft step is collapsed
 * into the request: create, then send for approval. Mirrors the trade flows.
 */
export async function createAndSubmitFacilityReverse(
  facilityReverse: FacilityReverseParams,
  dispatch: Dispatch<any>,
) {
  const created: any = await dispatch(saveFacilityReverse(facilityReverse))
  if (!created.success) {
    ErrorNotification({
      title: 'Reversal Failed',
      message: created.payload ?? 'Could not create the reversal draft.',
    })
    return false
  }
  const draftId = created.payload?.data?.id
  if (!draftId) {
    ErrorNotification({
      title: 'Reversal Failed',
      message: 'The reversal draft was created without an id.',
    })
    return false
  }
  const submitted = await submitFacilityReverse(
    { ...facilityReverse, id: draftId },
    dispatch,
    false,
  )
  if (!submitted) {
    // The draft exists but never reached a manager, so name that state rather
    // than implying nothing happened.
    ErrorNotification({
      title: 'Reversal Failed',
      message:
        'The reversal draft was created but could not be sent for approval. Submit it from Servicing.',
    })
    return false
  }

  SuccessNotification({
    title: 'Reversal Submitted',
    message:
      'The reversal has been sent for manager approval. The transaction stands until it is approved.',
  })
  return true
}
